import { Box, Button, Container, Grid, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getApiUrl } from '../../data/api';
import { Product } from '../../types/home';
import MarkdownRenderer from '../MarkdownRenderer';

const FeaturedProducts = () => {
  // Fetch products from API
  const {
    data,
    isLoading,
    error,
  } = useQuery<{ products: Product[] }, Error>({
    queryKey: ['products'],
    queryFn: async () => {
      const res = await fetch(`${getApiUrl()}/products`);
      if (!res.ok) throw new Error('Failed to fetch products');
      return res.json() as Promise<{ products: Product[] }>;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Get featured products (up to 3)
  const featuredProducts = (data?.products || [])
    .filter(product => product.featured)
    .slice(0, 3);

  return (
    <Box sx={{ py: 8, bgcolor: '#f5f5f5' }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            sx={{ 
              position: 'relative',
              display: 'inline-block',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -10,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 80,
                height: 3,
                bgcolor: 'secondary.main',
                borderRadius: 4
              }
            }}
          >
            Featured Begard Gear
          </Typography>
          <Typography 
            variant="subtitle1" 
            color="text.secondary"
            component="p"
            role="heading"
            aria-level={2}
            sx={{ mt: 2, maxWidth: 600, mx: 'auto' }}
          >
            Discover our most popular outdoor gear, designed for those who prioritize both safety and adventure.
          </Typography>
        </Box>

        {isLoading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Box 
              sx={{ 
                display: 'inline-block', 
                position: 'relative', 
                width: 40, 
                height: 40 
              }}
            >
              <Box 
                sx={{ 
                  position: 'absolute', 
                  top: 0, 
                  left: 0, 
                  width: '100%', 
                  height: '100%', 
                  border: '3px solid rgba(0,0,0,0.1)', 
                  borderTopColor: 'primary.main', 
                  borderRadius: '50%', 
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' }
                  }
                }} 
              />
            </Box>
            <Typography sx={{ mt: 2 }}>Loading featured products...</Typography>
          </Box>
        ) : error ? (
          <Typography color="error" align="center">
            Error loading products: {error.message}
          </Typography>
        ) : (
          <>
            <Grid container spacing={4}>
              {featuredProducts.length > 0 ? (
                featuredProducts.map((product) => (
                  <Grid item key={product.id} xs={12} sm={6} md={4}>
                    <Box 
                      sx={{ 
                        height: '100%', 
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        position: 'relative',
                        transition: 'all 0.3s ease',
                        bgcolor: 'white',
                        WebkitBackfaceVisibility: 'hidden',
                        backfaceVisibility: 'hidden',
                        transform: 'translateZ(0)',
                        willChange: 'transform',
                        '@media (hover: hover)': {
                          '&:hover': {
                            transform: 'translateY(-8px) translateZ(0)',
                            boxShadow: '0 12px 28px rgba(0,0,0,0.15)',
                          }
                        }
                      }}
                    >
                      {/* Product Badge - can be used for "New", "Sale", etc. */}
                      <Box 
                        sx={{ 
                          position: 'absolute', 
                          top: 16, 
                          left: 16, 
                          bgcolor: 'secondary.main', 
                          color: 'white',
                          px: 2,
                          py: 0.5,
                          borderRadius: 4,
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          zIndex: 5,
                          textTransform: 'uppercase',
                          letterSpacing: 1
                        }}
                      >
                        Featured
                      </Box>
                      
                      {/* Product Image - Now clickable */}
                      <Box
                        component={RouterLink}
                        to={`/products/${product.id}`}
                        sx={{
                          display: 'block',
                          width: '100%',
                          position: 'relative',
                          pt: '100%', // 1:1 aspect ratio
                          overflow: 'hidden',
                          textDecoration: 'none',
                          cursor: 'pointer',
                        }}
                      >
                        <Box
                          component="img"
                          src={product.thumbnail_url}
                          alt={product.name}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.5s ease',
                            WebkitBackfaceVisibility: 'hidden',
                            backfaceVisibility: 'hidden',
                            transform: 'translateZ(0)',
                            '@media (hover: hover)': {
                              '&:hover': {
                                transform: 'scale(1.05) translateZ(0)'
                              }
                            }
                          }}
                        />
                      </Box>
                      
                      {/* Product Info */}
                      <Box sx={{ p: 3 }}>
                        <Typography 
                          variant="h6" 
                          component={RouterLink}
                          to={`/products/${product.id}`}
                          sx={{ 
                            fontWeight: 'bold',
                            textDecoration: 'none',
                            display: 'block',
                            mb: 1,
                            '&:hover': {
                              color: 'primary.dark'
                            }
                          }}
                        >
                          {product.name}
                        </Typography>
                        
                        {product.description && (
                          <MarkdownRenderer 
                            content={product.description}
                            maxLines={2}
                            sx={{ 
                              height: 40,
                              mb: 2 
                            }}
                          />
                        )}
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                          {/* Only show price if it's not zero */}
                          {product.price > 0 && (
                            <Typography 
                              variant="h6" 
                              color="primary.main"
                              sx={{ fontWeight: 'bold' }}
                            >
                              ${product.price.toFixed(2)}
                            </Typography>
                          )}
                          
                          <Button 
                            variant="outlined"
                            color="primary"
                            component={RouterLink} 
                            to={`/products/${product.id}`}
                            sx={{ 
                              fontWeight: 'bold',
                              borderRadius: 2,
                              px: 2,
                              ml: product.price > 0 ? 0 : 'auto',
                              mr: product.price > 0 ? 0 : 'auto'
                            }}
                          >
                            View Details
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Box 
                    sx={{ 
                      textAlign: 'center', 
                      py: 4, 
                      px: 2,
                      bgcolor: 'white',
                      borderRadius: 2,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                    }}
                  >
                    <Typography variant="h6" gutterBottom>No featured products available</Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                      Check back soon for our featured outdoor gear.
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>

            <Box sx={{ mt: 6, textAlign: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                component={RouterLink}
                to="/products"
                sx={{ 
                  fontWeight: 'bold', 
                  px: 4, 
                  py: 1.5,
                  borderRadius: 2,
                  boxShadow: '0 6px 15px rgba(0,0,0,0.15)',
                  WebkitBackfaceVisibility: 'hidden',
                  backfaceVisibility: 'hidden',
                  transform: 'translateZ(0)',
                  '@media (hover: hover)': {
                    '&:hover': {
                      transform: 'translateY(-3px) translateZ(0)',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                    }
                  }
                }}
              >
                Shop All Products
              </Button>
            </Box>
          </>
        )}
      </Container>
    </Box>
  );
};

export default FeaturedProducts; 